import React from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import { cn } from '../../../utils/cn';

const DemandTable = ({ 
  demands, 
  loading, 
  selectedDemands,
  onSelectDemand, 
  onToggleSelect,
  onSelectAll 
}) => {
  const getPriorityColor = (priority) => {
    const colors = {
      'Crítica': 'text-error bg-error/10',
      'Alta': 'text-warning bg-warning/10',
      'Média': 'text-primary bg-primary/10',
      'Baixa': 'text-success bg-success/10'
    };
    return colors?.[priority] || 'text-muted-foreground bg-muted';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pendente': 'text-warning bg-warning/10',
      'Em Análise': 'text-primary bg-primary/10',
      'Aprovado': 'text-success bg-success/10',
      'Rejeitado': 'text-error bg-error/10'
    };
    return colors?.[status] || 'text-muted-foreground bg-muted';
  };

  const isNearDeadline = (deadline) => {
    if (!deadline) return false;
    const [day, month, year] = deadline?.split('/');
    const deadlineDate = new Date(year, month - 1, day);
    const today = new Date();
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 2 && diffDays >= 0;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-muted rounded"></div>
          {[1, 2, 3, 4, 5]?.map(i => (
            <div key={i} className="h-20 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="bg-muted/50 px-6 py-4 border-b border-border flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">
          Fila de Demandas
        </h2>
        <div className="flex items-center space-x-2">
          <Checkbox
            checked={selectedDemands?.length === demands?.length && demands?.length > 0}
            onCheckedChange={onSelectAll}
          />
          <span className="text-sm text-muted-foreground">
            Selecionar Todos
          </span>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-12">
                <Checkbox
                  checked={selectedDemands?.length === demands?.length && demands?.length > 0}
                  onCheckedChange={onSelectAll}
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Código
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Cliente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Data
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Prioridade
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Técnico
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                SLA
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {demands?.map((demand) => (
              <tr
                key={demand?.id}
                className={cn(
                  "hover:bg-muted/50 transition-colors cursor-pointer",
                  selectedDemands?.includes(demand?.id) && "bg-primary/5"
                )}
                onClick={() => onSelectDemand(demand)}
              >
                <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e?.stopPropagation()}>
                  <Checkbox
                    checked={selectedDemands?.includes(demand?.id)}
                    onCheckedChange={() => onToggleSelect(demand?.id)}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-foreground">
                    {demand?.code}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <img
                      src={demand?.client?.avatar}
                      alt={`Avatar de ${demand?.client?.name}`}
                      className="w-8 h-8 rounded-full"
                    />
                    <div className="text-sm font-medium text-foreground">
                      {demand?.client?.name}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-foreground">
                    {demand?.requestType}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-muted-foreground">
                    {demand?.submissionDate}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={cn(
                    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                    getPriorityColor(demand?.priority)
                  )}>
                    {demand?.priority}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {demand?.assignedTechnician ? (
                    <div className="flex items-center space-x-2">
                      <img
                        src={demand?.assignedTechnician?.avatar}
                        alt={`Avatar de ${demand?.assignedTechnician?.name}`}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-sm text-foreground">
                        {demand?.assignedTechnician?.name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={cn(
                    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                    getStatusColor(demand?.status)
                  )}>
                    {demand?.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {demand?.slaDeadline ? (
                    <div className={cn(
                      "flex items-center space-x-1",
                      isNearDeadline(demand?.slaDeadline) && "text-warning"
                    )}>
                      {isNearDeadline(demand?.slaDeadline) && (
                        <Icon name="AlertCircle" size={14} />
                      )}
                      <span className="text-sm">
                        {demand?.slaDeadline}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-border">
        {demands?.map((demand) => (
          <div
            key={demand?.id}
            className={cn(
              "p-4 hover:bg-muted/50 transition-colors cursor-pointer",
              selectedDemands?.includes(demand?.id) && "bg-primary/5"
            )}
            onClick={() => onSelectDemand(demand)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedDemands?.includes(demand?.id)}
                  onCheckedChange={() => onToggleSelect(demand?.id)}
                  onClick={(e) => e?.stopPropagation()}
                />
                <img
                  src={demand?.client?.avatar}
                  alt={`Avatar de ${demand?.client?.name}`}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <div className="font-medium text-foreground">
                    {demand?.code}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {demand?.client?.name}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end space-y-1">
                <span className={cn(
                  "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                  getPriorityColor(demand?.priority)
                )}>
                  {demand?.priority}
                </span>
                <span className={cn(
                  "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                  getStatusColor(demand?.status)
                )}>
                  {demand?.status}
                </span>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tipo:</span>
                <span className="text-foreground font-medium">
                  {demand?.requestType}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Data:</span>
                <span className="text-foreground">
                  {demand?.submissionDate}
                </span>
              </div>
              {demand?.slaDeadline && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">SLA:</span>
                  <span className={cn(
                    "flex items-center space-x-1",
                    isNearDeadline(demand?.slaDeadline) && "text-warning font-medium"
                  )}>
                    {isNearDeadline(demand?.slaDeadline) && (
                      <Icon name="AlertCircle" size={14} />
                    )}
                    <span>{demand?.slaDeadline}</span>
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {demands?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Inbox" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            Nenhuma demanda encontrada
          </h3>
          <p className="text-sm text-muted-foreground">
            Não há demandas que correspondam aos filtros selecionados.
          </p>
        </div>
      )}
    </div>
  );
};

export default DemandTable;